import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { platformWorkAPI, taxonomyAPI, purchaseOrdersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Plus, Rocket, FolderKanban, ListChecks, AlertTriangle, Settings2, Pencil, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown, Archive, RotateCcw, Info, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import { PlatformFormDialog } from '../components/platform/PlatformFormDialog';
import { WorkbackStagesDialog } from '../components/platform/WorkbackStagesDialog';
import { customFieldsAPI } from '../services/api';
import { renderCustomValue } from '../components/shared/EntityCustomFields';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';

const STATUS_STYLES = {
  not_started: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-800',
  in_review: 'bg-amber-100 text-amber-800',
  needs_triage: 'bg-red-100 text-red-700',
  completed_reported: 'bg-green-100 text-green-800',
};

const KpiCard = ({ icon: Icon, label, value, accent }) => (
  <Card data-testid={`platform-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="pt-5 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold leading-none">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export const PlatformPage = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const [tab, setTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [stages, setStages] = useState([]);
  const [taxonomies, setTaxonomies] = useState({ platform: [], levelup_team: [], ms_owner: [] });
  const [platformPOs, setPlatformPOs] = useState([]);
  const [projectFields, setProjectFields] = useState([]);
  const [taskFields, setTaskFields] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [formType, setFormType] = useState('project');
  const [editItem, setEditItem] = useState(null);
  const [stagesOpen, setStagesOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivedItems, setArchivedItems] = useState([]);

  // bulk selection
  const [selected, setSelected] = useState([]);
  const [archivedSelected, setArchivedSelected] = useState([]);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // pagination
  const PAGE_SIZE = 20;
  const [projPage, setProjPage] = useState(1);
  const [taskPage, setTaskPage] = useState(1);

  const [search, setSearch] = useState('');
  const [colFilters, setColFilters] = useState({ platform: new Set(), status: new Set(), ms_owner: new Set(), assignee: new Set(), project: new Set() });
  const [customColFilters, setCustomColFilters] = useState({});
  const [targetSort, setTargetSort] = useState('none');

  const setColFilter = (key, valuesSet) => setColFilters(prev => ({ ...prev, [key]: valuesSet }));
  const setCustomColFilter = (tid, valuesSet) => setCustomColFilters(prev => ({ ...prev, [tid]: valuesSet }));

  const stageLabel = (v) => stages.find(s => s.value === v)?.label || v || '—';
  const taxLabel = (list, value) => (list || []).find(i => i.value === value)?.label || value;

  const loadMeta = useCallback(async () => {
    try {
      const [tax, wb, pos] = await Promise.all([
        taxonomyAPI.getAll(),
        platformWorkAPI.getWorkback(),
        purchaseOrdersAPI.getAllSelectable(),
      ]);
      const byId = {};
      tax.forEach(c => { byId[c.id] = c.items || []; });
      setTaxonomies({ platform: byId.platform || [], levelup_team: byId.levelup_team || [], ms_owner: byId.ms_owner || [] });
      setStages(wb.stages || []);
      setPlatformPOs(pos || []);
    } catch { toast.error('Failed to load settings'); }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [projData, taskData, statData] = await Promise.all([
        platformWorkAPI.getAll({ item_type: 'project' }),
        platformWorkAPI.getAll({ item_type: 'task' }),
        platformWorkAPI.getStats(),
      ]);
      setProjects(projData);
      setTasks(taskData);
      setStats(statData);
    } catch { toast.error('Failed to load platform work'); }
    finally { setLoading(false); }
  }, []);

  const loadCustomFields = useCallback(async () => {
    try {
      const [pf, tf] = await Promise.all([
        customFieldsAPI.list('platform_project'),
        customFieldsAPI.list('platform_task'),
      ]);
      setProjectFields(pf);
      setTaskFields(tf);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadCustomFields(); }, [loadCustomFields]);

  const openCreate = (type) => { setFormType(type); setEditItem(null); setFormOpen(true); };
  const openEdit = (item) => { setFormType(item.item_type); setEditItem(item); setFormOpen(true); };

  const confirmBulkArchive = async () => {
    try {
      const res = await platformWorkAPI.bulkArchive(selected);
      toast.success(`${res.count} item(s) archived`);
      setSelected([]);
      setBulkArchiveOpen(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to archive'); }
  };

  const loadArchived = useCallback(async () => {
    try {
      const [pr, tk] = await Promise.all([
        platformWorkAPI.getAll({ item_type: 'project', archived: 'true' }),
        platformWorkAPI.getAll({ item_type: 'task', archived: 'true' }),
      ]);
      setArchivedItems([...pr, ...tk]);
    } catch { /* silent */ }
  }, []);

  const openArchived = () => { setArchivedSelected([]); loadArchived(); setArchivedOpen(true); };

  const confirmBulkRestore = async () => {
    if (!archivedSelected.length) return;
    try {
      const res = await platformWorkAPI.bulkRestore(archivedSelected);
      toast.success(`${res.count} item(s) restored`);
      setArchivedSelected([]);
      await Promise.all([loadArchived(), loadData()]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to restore'); }
  };

  const confirmBulkDelete = async () => {
    if (!archivedSelected.length) return;
    try {
      const res = await platformWorkAPI.bulkDelete(archivedSelected);
      toast.success(`${res.count} item(s) permanently deleted`);
      setArchivedSelected([]);
      setBulkDeleteOpen(false);
      await Promise.all([loadArchived(), loadData()]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const applyFilters = (list) => list.filter(i => {
    if (search && !(`${i.title} ${i.description || ''}`.toLowerCase().includes(search.toLowerCase()))) return false;
    if (colFilters.platform.size && !colFilters.platform.has(i.platform)) return false;
    if (colFilters.status.size && !colFilters.status.has(i.status)) return false;
    if (colFilters.ms_owner.size && !colFilters.ms_owner.has(i.ms_owner)) return false;
    if (colFilters.assignee.size && !colFilters.assignee.has(i.assignee)) return false;
    if (colFilters.project.size && !colFilters.project.has(i.item_type === 'task' ? i.project_title : i.title)) return false;
    for (const [tid, set] of Object.entries(customColFilters)) {
      if (!set || !set.size) continue;
      const raw = i.custom_fields?.[tid];
      const vals = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      if (!vals.some(v => set.has(v))) return false;
    }
    return true;
  });

  const filteredProjects = applyFilters(projects);
  const filteredTasks = applyFilters(tasks);
  const displayTasks = targetSort === 'none' ? filteredTasks : [...filteredTasks].sort((a, b) => {
    const av = a.target_date || ''; const bv = b.target_date || '';
    return targetSort === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  const today = new Date().toISOString().split('T')[0];
  const doneValue = stages[stages.length - 1]?.value;

  // Reset paging & selection when filters change
  useEffect(() => { setProjPage(1); setTaskPage(1); setSelected([]); },
    [search, colFilters, customColFilters]);

  const projTotalPages = Math.max(1, Math.ceil(filteredProjects.length / PAGE_SIZE));
  const taskTotalPages = Math.max(1, Math.ceil(displayTasks.length / PAGE_SIZE));
  const pagedProjects = filteredProjects.slice((projPage - 1) * PAGE_SIZE, projPage * PAGE_SIZE);
  const pagedTasks = displayTasks.slice((taskPage - 1) * PAGE_SIZE, taskPage * PAGE_SIZE);

  const pageIds = (tab === 'projects' ? pagedProjects : pagedTasks).map(i => i.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.includes(id));
  const toggleOne = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAllPage = () => setSelected(s => allPageSelected ? s.filter(id => !pageIds.includes(id)) : [...new Set([...s, ...pageIds])]);

  const archivedAllSelected = archivedItems.length > 0 && archivedItems.every(i => archivedSelected.includes(i.id));
  const toggleArchivedOne = (id) => setArchivedSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleArchivedAll = () => setArchivedSelected(archivedAllSelected ? [] : archivedItems.map(i => i.id));

  const StatusBadge = ({ value }) => (
    <Badge className={STATUS_STYLES[value] || 'bg-gray-100 text-gray-700'}>{stageLabel(value)}</Badge>
  );

  // Column filter option lists (only values present in the data, mapped to labels)
  const optsFrom = (rows, key, taxList) => {
    const present = new Set(rows.map(r => r[key]).filter(Boolean));
    return (taxList || []).filter(i => present.has(i.value)).map(i => ({ value: i.value, label: i.label }));
  };
  const statusOpts = stages.map(s => ({ value: s.value, label: s.label }));
  const projPlatformOpts = optsFrom(projects, 'platform', taxonomies.platform);
  const projMsOwnerOpts = optsFrom(projects, 'ms_owner', taxonomies.ms_owner);
  const taskPlatformOpts = optsFrom(tasks, 'platform', taxonomies.platform);
  const taskAssigneeOpts = optsFrom(tasks, 'assignee', taxonomies.levelup_team);
  const taskProjectOpts = [...new Set(tasks.map(t => t.project_title).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const customOptsFor = (field) => (field.options || []).map(o => ({ value: o.value ?? o, label: o.label ?? o }));
  const CustomHeader = ({ field }) => (
    <ColumnFilterPopover label={field.label} options={customOptsFor(field)} selected={customColFilters[field.taxonomy_id] || new Set()} onChange={(s) => setCustomColFilter(field.taxonomy_id, s)} />
  );

  const activeFilters = (tab === 'projects'
    ? [
        { key: 'platform', label: 'Platform', set: colFilters.platform, options: projPlatformOpts, onChange: (s) => setColFilter('platform', s) },
        { key: 'ms_owner', label: 'MS Owner', set: colFilters.ms_owner, options: projMsOwnerOpts, onChange: (s) => setColFilter('ms_owner', s) },
        { key: 'status', label: 'Status', set: colFilters.status, options: statusOpts, onChange: (s) => setColFilter('status', s) },
        ...projectFields.map(f => ({ key: f.taxonomy_id, label: f.label, set: customColFilters[f.taxonomy_id] || new Set(), options: customOptsFor(f), onChange: (s) => setCustomColFilter(f.taxonomy_id, s) })),
      ]
    : [
        { key: 'assignee', label: 'Assignee', set: colFilters.assignee, options: taskAssigneeOpts, onChange: (s) => setColFilter('assignee', s) },
        { key: 'platform', label: 'Platform', set: colFilters.platform, options: taskPlatformOpts, onChange: (s) => setColFilter('platform', s) },
        { key: 'project', label: 'Project', set: colFilters.project, options: taskProjectOpts, onChange: (s) => setColFilter('project', s) },
        { key: 'status', label: 'Status', set: colFilters.status, options: statusOpts, onChange: (s) => setColFilter('status', s) },
        ...taskFields.map(f => ({ key: f.taxonomy_id, label: f.label, set: customColFilters[f.taxonomy_id] || new Set(), options: customOptsFor(f), onChange: (s) => setCustomColFilter(f.taxonomy_id, s) })),
      ]);

  const Pagination = ({ page, totalPages, onPage, testId }) => (
    <div className="flex items-center justify-end gap-2 mt-4" data-testid={testId}>
      <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
      <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testId}-prev`}><ChevronLeft className="w-4 h-4" /></Button>
      <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid={`${testId}-next`}><ChevronRight className="w-4 h-4" /></Button>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="platform-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2"><Rocket className="w-7 h-7 text-lime" />Platform (LevelUp++)</h1>
          <p className="text-muted-foreground">Projects &amp; tasks for the LevelUp self-paced learning platform and its satellite apps</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openArchived} data-testid="view-archived-btn">
            <Archive className="w-4 h-4 mr-2" />Archived
          </Button>
          {isSuperAdmin && isSuperAdmin() && (
            <Button variant="outline" onClick={() => setStagesOpen(true)} data-testid="manage-stages-btn">
              <Settings2 className="w-4 h-4 mr-2" />Workback Stages
            </Button>
          )}
          <Button onClick={() => openCreate('project')} data-testid="new-project-btn" className="bg-lime text-lime-foreground hover:bg-lime/90">
            <Plus className="w-4 h-4 mr-2" />New Project
          </Button>
          <Button onClick={() => openCreate('task')} data-testid="new-task-btn" variant="secondary">
            <Plus className="w-4 h-4 mr-2" />New Task
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={FolderKanban} label="Projects" value={stats.projects_count} accent="bg-lime/20 text-lime-foreground" />
          <KpiCard icon={ListChecks} label="Tasks" value={stats.tasks_count} accent="bg-blue-100 text-blue-700" />
          <KpiCard icon={ListChecks} label="Open Tasks" value={stats.open_tasks_count} accent="bg-amber-100 text-amber-700" />
          <KpiCard icon={AlertTriangle} label="Overdue" value={stats.overdue_count} accent="bg-red-100 text-red-700" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title or description..." className="pl-9" data-testid="platform-search-input" />
        </div>
        <p className="text-xs text-muted-foreground">Tip: click a column header to filter by that field.</p>
      </div>

      <ActiveFiltersBar filters={activeFilters} search={search} onClearSearch={() => setSearch('')} testId="platform-active-filters" />

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-amber-900">{selected.length} selected</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected([])} data-testid="bulk-clear-btn">Clear</Button>
            <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700" onClick={() => setBulkArchiveOpen(true)} data-testid="bulk-archive-btn">
              <Archive className="w-4 h-4 mr-2" />Archive Selected
            </Button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setSelected([]); setColFilters({ platform: new Set(), status: new Set(), ms_owner: new Set(), assignee: new Set(), project: new Set() }); setCustomColFilters({}); setSearch(''); }}>
        <TabsList>
          <TabsTrigger value="projects" data-testid="tab-projects">Projects ({filteredProjects.length})</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({filteredTasks.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="projects">
          <Card>
            <CardContent className="pt-5">
              {loading ? (
                <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded" />)}</div>
              ) : filteredProjects.length > 0 ? (
                <>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-10">
                      <Checkbox checked={allPageSelected} onCheckedChange={toggleAllPage} data-testid="select-all-projects" aria-label="Select all" />
                    </TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead><ColumnFilterPopover label="Platform" options={projPlatformOpts} selected={colFilters.platform} onChange={(s) => setColFilter('platform', s)} testId="platform-col-filter-platform" /></TableHead>
                    <TableHead><ColumnFilterPopover label="MS Owner" options={projMsOwnerOpts} selected={colFilters.ms_owner} onChange={(s) => setColFilter('ms_owner', s)} testId="platform-col-filter-msowner" /></TableHead>
                    <TableHead>Linked PO</TableHead>
                    <TableHead>Tasks</TableHead>
                    <TableHead><ColumnFilterPopover label="Status" options={statusOpts} selected={colFilters.status} onChange={(s) => setColFilter('status', s)} testId="platform-col-filter-status" /></TableHead>
                    {projectFields.map(f => <TableHead key={f.id}><CustomHeader field={f} /></TableHead>)}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {pagedProjects.map(p => (
                      <TableRow key={p.id} data-testid={`project-row-${p.id}`} data-state={selected.includes(p.id) ? 'selected' : undefined}>
                        <TableCell>
                          <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggleOne(p.id)} data-testid={`select-project-${p.id}`} aria-label="Select row" />
                        </TableCell>
                        <TableCell className="font-medium text-sm max-w-[260px]">
                          <button
                            type="button"
                            onClick={() => { setColFilter('project', new Set([p.title])); setTab('tasks'); }}
                            className="text-left text-lime-700 hover:underline font-medium"
                            data-testid={`project-name-link-${p.id}`}
                            title="View tasks for this project"
                          >
                            {p.title}
                          </button>
                          {p.description && <div className="text-xs text-muted-foreground font-normal truncate">{p.description}</div>}
                        </TableCell>
                        <TableCell className="text-sm">{taxLabel(taxonomies.platform, p.platform) || '—'}</TableCell>
                        <TableCell className="text-sm">{taxLabel(taxonomies.ms_owner, p.ms_owner) || '—'}</TableCell>
                        <TableCell className="text-sm">{p.linked_po_number || '—'}</TableCell>
                        <TableCell className="text-sm text-center">{p.completed_task_count || 0}/{p.task_count || 0}</TableCell>
                        <TableCell><StatusBadge value={p.status} /></TableCell>
                        {projectFields.map(f => (
                          <TableCell key={f.id} className="text-sm">{renderCustomValue(p.custom_fields?.[f.taxonomy_id], f.options) || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                        ))}
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {projTotalPages > 1 && <Pagination page={projPage} totalPages={projTotalPages} onPage={setProjPage} testId="projects-pagination" />}
                </>
              ) : (
                <div className="text-center py-16">
                  <FolderKanban className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No projects yet</h3>
                  <p className="text-muted-foreground mb-4">Create a project to group related LevelUp platform work.</p>
                  <Button onClick={() => openCreate('project')} className="bg-lime text-lime-foreground hover:bg-lime/90"><Plus className="w-4 h-4 mr-2" />New Project</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks">
          <Card>
            <CardContent className="pt-5">
              {loading ? (
                <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded" />)}</div>
              ) : filteredTasks.length > 0 ? (
                <>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-10">
                      <Checkbox checked={allPageSelected} onCheckedChange={toggleAllPage} data-testid="select-all-tasks" aria-label="Select all" />
                    </TableHead>
                    <TableHead>Task</TableHead>
                    <TableHead><ColumnFilterPopover label="Assignee" options={taskAssigneeOpts} selected={colFilters.assignee} onChange={(s) => setColFilter('assignee', s)} testId="platform-col-filter-assignee" /></TableHead>
                    <TableHead><ColumnFilterPopover label="Platform" options={taskPlatformOpts} selected={colFilters.platform} onChange={(s) => setColFilter('platform', s)} testId="platform-col-filter-task-platform" /></TableHead>
                    <TableHead><ColumnFilterPopover label="Project" options={taskProjectOpts} selected={colFilters.project} onChange={(s) => setColFilter('project', s)} testId="platform-col-filter-project" /></TableHead>
                    <TableHead>
                      <button type="button" onClick={() => setTargetSort(d => d === 'asc' ? 'desc' : (d === 'desc' ? 'none' : 'asc'))} className="flex items-center gap-1 hover:text-foreground" data-testid="platform-sort-target-date">
                        Target Date
                        {targetSort === 'asc' ? <ArrowUp className="w-3 h-3" /> : targetSort === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                      </button>
                    </TableHead>
                    <TableHead><ColumnFilterPopover label="Status" options={statusOpts} selected={colFilters.status} onChange={(s) => setColFilter('status', s)} testId="platform-col-filter-task-status" /></TableHead>
                    <TableHead>Created</TableHead>
                    {taskFields.map(f => <TableHead key={f.id}><CustomHeader field={f} /></TableHead>)}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {pagedTasks.map(t => {
                      const overdue = t.target_date && t.target_date < today && t.status !== doneValue;
                      return (
                        <TableRow key={t.id} data-testid={`task-row-${t.id}`} data-state={selected.includes(t.id) ? 'selected' : undefined}>
                          <TableCell>
                            <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => toggleOne(t.id)} data-testid={`select-task-${t.id}`} aria-label="Select row" />
                          </TableCell>
                          <TableCell className="font-medium text-sm max-w-[240px]">
                            <button type="button" onClick={() => navigate(`/platform/tasks/${t.id}`)} className="text-left text-lime-700 hover:underline font-medium" data-testid={`task-name-link-${t.id}`}>
                              {t.title}
                            </button>
                            {t.description && <div className="text-xs text-muted-foreground font-normal truncate">{t.description}</div>}
                          </TableCell>
                          <TableCell className="text-sm">{taxLabel(taxonomies.levelup_team, t.assignee) || '—'}</TableCell>
                          <TableCell className="text-sm">{taxLabel(taxonomies.platform, t.platform) || '—'}</TableCell>
                          <TableCell className="text-sm">{t.project_title || '—'}</TableCell>
                          <TableCell className="text-sm">
                            {t.target_date ? (
                              <span className={overdue ? 'text-red-600 font-medium' : ''}>{t.target_date}{overdue && ' ⚠'}</span>
                            ) : '—'}
                          </TableCell>
                          <TableCell><StatusBadge value={t.status} /></TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap" data-testid={`task-created-${t.id}`}>{t.created_at ? String(t.created_at).slice(0, 10) : '—'}</TableCell>
                          {taskFields.map(f => (
                            <TableCell key={f.id} className="text-sm">{renderCustomValue(t.custom_fields?.[f.taxonomy_id], f.options) || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                          ))}
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(t)} data-testid={`edit-task-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {taskTotalPages > 1 && <Pagination page={taskPage} totalPages={taskTotalPages} onPage={setTaskPage} testId="tasks-pagination" />}
                </>
              ) : (
                <div className="text-center py-16">
                  <ListChecks className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No tasks yet</h3>
                  <p className="text-muted-foreground mb-4">Create a task and assign it to a team member.</p>
                  <Button onClick={() => openCreate('task')} className="bg-lime text-lime-foreground hover:bg-lime/90"><Plus className="w-4 h-4 mr-2" />New Task</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PlatformFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        itemType={formType}
        initial={editItem}
        stages={stages}
        taxonomies={taxonomies}
        projects={projects}
        platformPOs={platformPOs}
        currentUser={user}
        isSuperAdmin={!!(isSuperAdmin && isSuperAdmin())}
        onSchemaChanged={loadCustomFields}
        onSaved={loadData}
      />

      <WorkbackStagesDialog
        open={stagesOpen}
        onOpenChange={setStagesOpen}
        stages={stages}
        currentUser={user}
        onSaved={() => { loadMeta(); loadData(); }}
      />

      {/* Bulk archive confirm */}
      <AlertDialog open={bulkArchiveOpen} onOpenChange={setBulkArchiveOpen}>
        <AlertDialogContent data-testid="bulk-archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {selected.length} item(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              The selected item(s) will be moved to the Archive and hidden from the tracker. Archiving a project also archives its tasks. Items are not deleted — a Super Admin can restore or permanently delete them from the Archived view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkArchive} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="confirm-bulk-archive-btn">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk permanent delete confirm (Super Admin) */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="bulk-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {archivedSelected.length} item(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              The selected archived item(s) will be permanently removed. Tasks attached to any deleted project will become standalone tasks (not deleted). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-bulk-delete-btn">Delete Permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archived management (view for all; restore any; permanent delete = Super Admin) */}
      <Dialog open={archivedOpen} onOpenChange={setArchivedOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="archived-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive className="w-5 h-5" />Archived Projects &amp; Tasks</DialogTitle>
            <DialogDescription>Archived items are hidden from the tracker. Restore to bring them back.{isSuperAdmin && isSuperAdmin() ? ' As Super Admin you can also delete them permanently.' : ' Only a Super Admin can delete them permanently.'}</DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-2 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2" data-testid="archival-policy-note">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Automatic archival &amp; deletion policy</p>
              <p>Completed tasks (status "{stageLabel(stages[stages.length - 1]?.value)}") are auto-archived <b>6 months</b> after completion. Archived items are automatically <b>deleted permanently 30 days</b> after being archived (only the title is kept in an internal ledger).</p>
            </div>
          </div>

          {archivedItems.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No archived items.</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 border-b pb-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={archivedAllSelected} onCheckedChange={toggleArchivedAll} data-testid="select-all-archived" aria-label="Select all archived" />
                  {archivedSelected.length > 0 ? `${archivedSelected.length} selected` : 'Select all'}
                </label>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" disabled={!archivedSelected.length} onClick={confirmBulkRestore} data-testid="bulk-restore-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" />Restore</Button>
                  {isSuperAdmin && isSuperAdmin() && (
                    <Button size="sm" variant="destructive" disabled={!archivedSelected.length} onClick={() => setBulkDeleteOpen(true)} data-testid="bulk-perm-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {archivedItems.map(it => (
                  <div key={it.id} className="flex items-center gap-3 border rounded-lg px-3 py-2" data-testid={`archived-row-${it.id}`} data-state={archivedSelected.includes(it.id) ? 'selected' : undefined}>
                    <Checkbox checked={archivedSelected.includes(it.id)} onCheckedChange={() => toggleArchivedOne(it.id)} data-testid={`select-archived-${it.id}`} aria-label="Select row" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] capitalize">{it.item_type}</Badge>
                        <span className="text-sm font-medium truncate">{it.title}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{it.platform || '—'}{it.archived_at ? ` · archived ${it.archived_at.slice(0, 10)}` : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformPage;
